import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import classNames from 'classnames';
import { toast } from "react-toastify";
import _ from "lodash";

import { selectCurrentUserId, selectCurrentUser } from '../../store/models/User';
import { selectCurrentProjectId } from '../../store/models/Project';
import Ai, { selectAllRagChatbots, selectRagChatbot, getRagChatbotIframeUrl } from '../../store/models/Ai';
import Loader from '../../components/Loader';
import { Button } from '../../components/Button';
import { InfoIcon, UploadIconIcon, DeleteIcon } from '../../components/Svg';
import Tooltip from '../../components/Tooltip';
import { Checkbox } from '../../components/form/Checkbox';
import Slider from '../../components/Slider';

// Import the existing tab components
import { KnowledgeTab } from '../../components/rag-chatbot/KnowledgeTab';
import { RagStatsTab } from '../../components/rag-chatbot/RagStatsTab';
import { RagIntegrationTab } from '../../components/rag-chatbot/RagIntegratrionTab';
import { createSelector } from 'reselect';
import { pushTo } from '../../store/history';
import { Urls } from '../../constants';
import { RagChatsTab } from '../../components/rag-chatbot/RagChatsTab';
import { MiniProjectRepsTab } from '../../components/rag-chatbot/MiniProjectRepsTab';

// LocalStorage key for storing the latest chatbot ID
const LATEST_CHATBOT_ID_KEY = 'mini-dashboard-latest-chatbot-id';

// LocalStorage utility functions
const saveLatestChatbotId = (chatbotId) => {
  try {
    localStorage.setItem(LATEST_CHATBOT_ID_KEY, chatbotId);
  } catch (error) {
    console.warn('Failed to save latest chatbot ID to localStorage:', error);
  }
};

const getLatestChatbotId = () => {
  try {
    return localStorage.getItem(LATEST_CHATBOT_ID_KEY);
  } catch (error) {
    console.warn('Failed to retrieve latest chatbot ID from localStorage:', error);
    return null;
  }
};

const selector = createSelector(
  (state, id) => selectRagChatbot(state, selectCurrentProjectId(state), id),
  (state) => selectCurrentProjectId(state),
  (state) => selectCurrentUserId(state),
  (state) => selectAllRagChatbots(state, selectCurrentProjectId(state)),
  (state) => selectCurrentUser(state),
  (chatbot, projectId, userId, chatbots, user) => ({ chatbot, projectId, userId, chatbots: _.values(chatbots), user })
)

// QuestionsSection component for managing preset questions
const QuestionsSection = ({ questions, setQuestions }) => {
  const handleQuestionChange = (index, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index].question = value;
    setQuestions(updatedQuestions);
  };

  const handleAnswerChange = (index, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index].answer = value;
    setQuestions(updatedQuestions);
  };

  const handleDeleteQuestion = (index) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
  };

  return (
    <div className='MiniDashboardChatbot__questions'>
      {questions.map((q, i) => (
        <div className='MiniDashboardChatbot__question-container' key={i}>
          <div className='MiniDashboardChatbot__question'>
            <div className='MiniDashboardChatbot__question--wrap'>
              <div className='MiniDashboardChatbot__question--label'>Question #{i + 1}</div>
              <input 
                className='MiniDashboardChatbot__question--input'
                type="text" 
                placeholder={`Enter question #${i + 1}`}
                value={q.question}
                onChange={(e) => handleQuestionChange(i, e.target.value)} 
              />
            </div>
            <div className='MiniDashboardChatbot__question--wrap'>
              <div className='MiniDashboardChatbot__question--label'>Answer #{i + 1}</div>
              <input 
                className='MiniDashboardChatbot__question--input'
                type="text" 
                placeholder={`Enter answer #${i + 1}, or leave blank if you want the chatbot to generate the answer dynamically`}
                value={q.answer}
                onChange={(e) => handleAnswerChange(i, e.target.value)} 
              />
            </div>
          </div>
          <div className='MiniDashboardChatbot__question-icon-container'>
            <div 
              className='MiniDashboardChatbot__question-delete-icon' 
              onClick={() => handleDeleteQuestion(i)}
            >
              <DeleteIcon />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

function MiniDashboardChatbot() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { chatbot, projectId, userId, chatbots, user } = useSelector(state => selector(state, id));
  const isTalkJSEnabled = chatbot ? _.get(chatbot, 'ui.talkjs.enabled', false) : false;
  const userRole = user?.role;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  // Set default tab based on user role - 'realtime' for rep, 'playground' for others
  const getDefaultSection = () => {
    if (userRole === 'rep' && isTalkJSEnabled) {
      return 'realtime';
    }
    return 'playground';
  };

  const [selectedSection, setSelectedSection] = useState(getDefaultSection());
  const [systemMessage, setSystemMessage] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const [accessToken, setAccessToken] = useState('');
  
  // Auto message states
  const [enableAutoMessage, setEnableAutoMessage] = useState(false);
  const [autoMessageDelayMinutes, setAutoMessageDelayMinutes] = useState(5);
  const [autoMessage, setAutoMessage] = useState('');
  const [originalAutoMessageSettings, setOriginalAutoMessageSettings] = useState({
    enabled: false,
    delayMinutes: 5,
    message: ''
  });

  useEffect(() => {
    if (_.isNil(userId)) {
      pushTo(Urls.MINI_DASHBOARD_HOME);
      return;
    }

    if (!id) {
      const latestChatbotId = getLatestChatbotId();
      if (latestChatbotId) {
        pushTo(Urls.MINI_DASHBOARD_CHATBOT, { id: latestChatbotId });
        return;
      }
      if (projectId) {
        fetchChatbots(projectId);
      }
      return;
    }

    if (projectId && id) {
      fetchData();
    }
  }, [id, userId, projectId]);


  useEffect(() => {
    if (chatbot) {
      setError(null);
    }
  }, [chatbot]);

  useEffect(() => {
    if (chatbot) {
      const systemPrompt = chatbot.public_system_prompt || '';
      const presetQuestions = _.get(chatbot, 'ui.preset_questions') || [];
      
      // Initialize auto message settings
      const autoMsgEnabled = _.get(chatbot, 'ui.auto_message.enabled', false);
      const autoMsgDelay = _.get(chatbot, 'ui.auto_message.delay_minutes', 5);
      const autoMsgMessages = _.get(chatbot, 'ui.auto_message.messages', []);
      const autoMsgText = autoMsgMessages.length > 0 ? (autoMsgMessages[0]?.message || '') : '';
      
      setSystemMessage(systemPrompt);
      setQuestions(presetQuestions);
      setOriginalQuestions(_.cloneDeep(presetQuestions)); // Deep clone to avoid reference issues
      
      setEnableAutoMessage(autoMsgEnabled);
      setAutoMessageDelayMinutes(autoMsgDelay);
      setAutoMessage(autoMsgText);
      setOriginalAutoMessageSettings({
        enabled: autoMsgEnabled,
        delayMinutes: autoMsgDelay,
        message: autoMsgText
      });
      
      // Save the current chatbot ID to localStorage
      saveLatestChatbotId(chatbot.id);
    }
  }, [chatbot]);

  // Update default section when userRole or TalkJS status changes
  useEffect(() => {
    if (userRole === 'rep' && isTalkJSEnabled) {
      setSelectedSection('realtime');
    }
  }, [userRole, isTalkJSEnabled]);

  // Redirect from realtime support section and reps section if TalkJS is disabled
  useEffect(() => {
    // Only redirect if we have a chatbot loaded and TalkJS is confirmed disabled
    if (chatbot && (selectedSection === 'realtime' || selectedSection === 'reps') && !isTalkJSEnabled) {
      console.log('Redirecting from', selectedSection, 'to playground because TalkJS is disabled');
      setSelectedSection('playground');
    }
  }, [selectedSection, isTalkJSEnabled, chatbot]);

  const fetchData = async () => {
    setLoading(true);
    if (error) setError(null);
    try {
      await dispatch(Ai.actions.fetchRagChatbot(projectId, id));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const fetchChatbots = async (projectId) => {
    setLoading(true);
    if (error) setError(null);
    const chatbots = await dispatch(Ai.actions.fetchRagChatbots(projectId));
    if (chatbots.length > 0) {
      // Check if we have a stored chatbot ID and if it exists in the current project
      const latestChatbotId = getLatestChatbotId();
      const storedChatbot = latestChatbotId && _.find(chatbots, { id: latestChatbotId });

      if (storedChatbot) {
        // Use the stored chatbot if it exists in current project
        pushTo(Urls.MINI_DASHBOARD_CHATBOT, { id: latestChatbotId });
      } else {
        // Fall back to the first chatbot
        pushTo(Urls.MINI_DASHBOARD_CHATBOT, { id: chatbots[0].id });
      }
    } else {
      setError("No chatbots found in this project");
    }
    setLoading(false);
  }

  // Helper function to check if settings have changed
  const hasSettingsChanged = () => {
    if (!chatbot) return false;
    
    const originalSystemPrompt = chatbot.public_system_prompt || '';
    const systemPromptChanged = systemMessage !== originalSystemPrompt;
    const questionsChanged = !_.isEqual(questions, originalQuestions);
    
    // Check auto message changes
    const autoMessageChanged = 
      enableAutoMessage !== originalAutoMessageSettings.enabled ||
      autoMessageDelayMinutes !== originalAutoMessageSettings.delayMinutes ||
      autoMessage !== originalAutoMessageSettings.message;
    
    console.log('Settings change check:', {
      systemMessage,
      originalSystemPrompt,
      systemPromptChanged,
      questions,
      originalQuestions,
      questionsChanged,
      autoMessageChanged,
      hasChanges: systemPromptChanged || questionsChanged || autoMessageChanged
    });
    
    return systemPromptChanged || questionsChanged || autoMessageChanged;
  };

  const handleUpdateSystemMessage = async () => {
    if (!chatbot) return;

    try {
      setUpdateLoading(true);

      const updateData = {
        public_system_prompt: systemMessage,
        project_id: projectId,
        ui: {
          ...chatbot.ui,
          preset_questions: questions,
          auto_message: {
            enabled: enableAutoMessage,
            delay_minutes: parseInt(autoMessageDelayMinutes),
            messages: [{ message: autoMessage, language: null }]
          }
        }
      };

      await dispatch(Ai.actions.updateRagChatbot(chatbot.id, updateData));
      await dispatch(Ai.actions.fetchRagChatbot(projectId, chatbot.id));
      // Update original questions and auto message settings to reflect the saved state
      setOriginalQuestions(questions);
      setOriginalAutoMessageSettings({
        enabled: enableAutoMessage,
        delayMinutes: autoMessageDelayMinutes,
        message: autoMessage
      });
      toast.success('Settings updated successfully!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUpdateLoading(false);
    }
  }

  const renderPlaygroundContent = () => {
    if (!chatbot) return null;

    const iframeUrl = getRagChatbotIframeUrl(chatbot);

    return (
      <div className='MiniDashboardChatbot__content'>
        <div className='MiniDashboardChatbot__playground'>
          {chatbot && <iframe src={iframeUrl} width='100%' height='100%' />}
        </div>
      </div>
    );
  }

  const renderSettingsContent = () => (
    <div className='MiniDashboardChatbot__content'>

      <div className='MiniDashboardChatbot__form-group'>
        <div className='MiniDashboardChatbot__input-label'>
          Partner system prompt
          <Tooltip tooltip={"Give instructions to the chatbot on how to respond to user messages, give a personality to the chatbot. Not visible to users."}>
            <InfoIcon />
          </Tooltip>
        </div>
        <textarea
          className='MiniDashboardChatbot__textarea'
          value={systemMessage}
          onChange={(e) => setSystemMessage(e.target.value)}
          placeholder="Enter instructions for your chatbot..."
        />
      </div>

      <div className='MiniDashboardChatbot__form-group'>
        <div className='MiniDashboardChatbot__input-label'>
          Preset questions
          <Tooltip tooltip={"Questions will be displayed at the bottom of the chatbot. Answers are optional, if not provided, the chatbot will generate an answer."}>
            <InfoIcon />
          </Tooltip>
        </div>
        <div className='MiniDashboardChatbot__preset-questions'>
          <div className='MiniDashboardChatbot__preset-questions--wrap'>
            <QuestionsSection questions={questions} setQuestions={setQuestions} />
            <div 
              className='MiniDashboardChatbot__preset-questions--icon' 
              onClick={() => setQuestions(q => q.concat({ question: '', answer: '' }))}
            >
              <UploadIconIcon /> New
            </div>
          </div>
        </div>
      </div>

      <div className='MiniDashboardChatbot__form-group'>
        <div className='MiniDashboardChatbot__input-label'>
          Automatic Welcome Message
          <Tooltip tooltip={"Send an automatic message after the user has been idle for a specified time"}>
            <InfoIcon />
          </Tooltip>
        </div>
        <Checkbox
          checked={enableAutoMessage}
          onChange={(e) => setEnableAutoMessage(e.target.checked)}
          label="Enable automatic message"
        />
      </div>

      {enableAutoMessage && (
        <>
          <div className='MiniDashboardChatbot__form-group'>
            <div className='MiniDashboardChatbot__input-label'>
              Send message after (minutes)
              <Tooltip tooltip={"Number of minutes to wait before sending the automatic message"}>
                <InfoIcon />
              </Tooltip>
            </div>
            <Slider
              className='MiniDashboardChatbot__slider-wrap'
              step={1}
              id="autoMessageDelayId"
              max={60}
              min={1}
              value={autoMessageDelayMinutes}
              onChange={(e) => setAutoMessageDelayMinutes(Number(e.target.value))}
            />
          </div>

          <div className='MiniDashboardChatbot__form-group'>
            <div className='MiniDashboardChatbot__input-label'>
              Message Content
              <Tooltip tooltip={"The message that will be sent automatically"}>
                <InfoIcon />
              </Tooltip>
            </div>
            <textarea
              className='MiniDashboardChatbot__textarea'
              value={autoMessage}
              onChange={(e) => setAutoMessage(e.target.value)}
              placeholder="Enter the message to send automatically..."
              rows={3}
            />
          </div>
        </>
      )}

      <div className='MiniDashboardChatbot__form-actions'>
        <Button
          color="green"
          title="Update Settings"
          onClick={handleUpdateSystemMessage}
          loading={updateLoading}
          disabled={updateLoading || !hasSettingsChanged()}
        />
      </div>
    </div>
  );

  const renderKnowledgeContent = () => (
    <div className='MiniDashboardChatbot__content'>
      {chatbot && <KnowledgeTab chatbot={chatbot} projectId={projectId} isMini={true} />}
    </div>
  );

  const renderStatsContent = () => (
    <div className='MiniDashboardChatbot__content'>
      {chatbot && <RagStatsTab chatbot={chatbot} projectId={projectId} />}
    </div>
  );

  const renderRealtimeSupportContent = () => (
    <div className='MiniDashboardChatbot__content'>
      {chatbot && <RagChatsTab chatbot={chatbot} projectId={projectId} isMini={true} userRole={userRole} />}
    </div>
  );

  const renderRepsContent = () => (
    <div className='MiniDashboardChatbot__content'>
      {projectId && <MiniProjectRepsTab projectId={projectId} />}
    </div>
  );

  const renderIntegrationContent = () => (
    <div className='MiniDashboardChatbot__content'>
      {chatbot && <RagIntegrationTab chatbot={chatbot} accessToken={accessToken} />}
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className={'MiniDashboardChatbot__content'}>
          <Loader size='large' color='grey' />
        </div>
      );
    }

    if (error) {
      return (
        <div className={'MiniDashboardChatbot__content'}>
          <div className={'MiniDashboardChatbot__error'}>
            {error}
            <Button onClick={fetchData} color="green-outline" title="Retry" />
          </div>
        </div>
      );
    }

    switch (selectedSection) {
      case 'playground':
        return renderPlaygroundContent();
      case 'integration':
        return renderIntegrationContent();
      case 'settings':
        return renderSettingsContent();
      case 'knowledge':
        return renderKnowledgeContent();
      case 'stats':
        return renderStatsContent();
      case 'realtime':
        return isTalkJSEnabled ? renderRealtimeSupportContent() : renderPlaygroundContent();
      case 'reps':
        return isTalkJSEnabled ? renderRepsContent() : renderPlaygroundContent();
      default:
        return renderPlaygroundContent();
    }
  };

  return (
    <div className={'MiniDashboardChatbot'}>
      <div className={'MiniDashboardChatbot__container'}>
        <div className={'MiniDashboardChatbot__navigation'}>
          {getSections(isTalkJSEnabled, userRole).map((section) => (
            <div
              key={section.id}
              className={classNames('MiniDashboardChatbot__nav-item', {
                'MiniDashboardChatbot__nav-item--active': selectedSection === section.id
              })}
              onClick={() => setSelectedSection(section.id)}
            >
              <div className={'MiniDashboardChatbot__nav-item-title'}>
                {section.label}
              </div>
            </div>
          ))}
        </div>

        <div className={'MiniDashboardChatbot__main'}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default MiniDashboardChatbot;


// Define sections function to access isTalkJSEnabled and userRole
function getSections(isTalkJSEnabled, userRole) {
  // For rep role, only show Playground and Realtime Support (if TalkJS is enabled)
  if (userRole === 'rep') {
    const repSections = [
      {
        id: 'playground',
        label: 'Playground',
      },
    ];
    
    if (isTalkJSEnabled) {
      repSections.push({
        id: 'realtime',
        label: 'Realtime Support'
      });
    }
    
    return repSections;
  }
  
  // For partner role and others, show all sections
  const baseSections = [
    {
      id: 'playground',
      label: 'Playground',
    },
    {
      id: 'integration',
      label: 'Integration',
    },
    {
      id: 'settings',
      label: 'Settings',
    },
    {
      id: 'knowledge',
      label: 'Knowledge Base',
    },
    {
      id: 'stats',
      label: 'Stats',
    },
  ];
  
  // In mini dashboard, show realtime support and team if TalkJS is enabled
  if (isTalkJSEnabled) {
    baseSections.push({
      id: 'realtime',
      label: 'Realtime Support'
    });
    baseSections.push({
      id: 'reps',
      label: 'Team'
    });
  }
  
  return baseSections;
}